// src/POOverview.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Alert,
  Fade,
  Box,
  Button,
  Typography,
  Chip,
  Link as MuiLink,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import POOverviewSkeleton from './Skeletons/POOverviewSkeleton'
import RecommendationPreviewDrawer from './components/RecommendationPreviewDrawer'
import RecommendedOrdersSection from './RecommendedOrdersSection'
import { zeileFaelltAusDenEmpfehlungen } from './utils/discontinued'
import { formatDate, formatMoney, formatNumber } from './utils/format'
import { useHeaderConfig } from './components/SidebarAndHeaderBar'
import PageContainer from './components/common/PageContainer';
import ViewHeader from './components/ViewHeader';
import CollapsibleSection from './components/common/CollapsibleSection';
import TableFooterSimple from './components/common/TableFooterSimple';
import DeclinedProductsDrawer from './components/DeclinedProductsDrawer';
import { BEDIENBREITE, COLUMN_MAX, CONTENT_WIDTH, DUR, SP } from './theme/tokens';
// FX direction lives in one place now — see the module header for why it
// could not be unit-tested while it sat inline in this file (finding D8).
import { euroTotalForPO, isUnconvertedNative } from './utils/poFxToEur';
import HelpTooltip from './components/HelpTooltip';
import FilterPillRow from './components/common/FilterPillRow';
import TableViewport from './components/common/TableViewport';
import RowActionsMenu from './components/common/RowActionsMenu';
import EmptyState from './components/common/EmptyState';
import ListToolbar from './components/common/ListToolbar';
import RefreshButton from './components/common/RefreshButton';
import BottomSheetMultiSelect from './components/common/BottomSheetMultiSelect';
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from './components/common/MobileRecordCard';
import useContainerWidth from './components/common/useContainerWidth';
import useColumnPriority from './components/common/useColumnPriority';
import useListQuery from './components/common/listQuery/useListQuery';
import {
  PO_STATUSES,
  PO_STATUS_LABEL,
  poStatusKey,
  daysLabel,
} from './components/POStatusBar';
import Panel from './components/common/Panel';


const API = process.env.REACT_APP_API_BASE_URL
const STATUS_COLOR_OV = {
  Draft: 'default', 'Order placed': 'warning', Paid: 'success',
  'In Delivery': 'info', Done: 'success', Closed: 'default', Cancelled: 'error',
};

/**
 * Money for a PO whose total could NOT be converted to euro — the amount in its
 * own currency, e.g. `1.234,00 USD`.
 *
 * 🔴 Seit PR 2 läuft das über `formatMoney`: deutsche Form (Trennzeichen `.`,
 * Dezimal `,`) und der Währungs**code** HINTEN — nie `$` vorne (Owner-Entscheid
 * 2026-10-06, W9). Vorher stand hier `Intl.NumberFormat('en-US', …)`, das
 * `$1,234.00` lieferte: eine englische Form in einer deutschen Oberfläche.
 *
 * ⚠️ The VALUE is unchanged and so is the sort key — the STRING is not.
 * Nothing about the amount, the ordering or R10 changes — only how it reads.
 *
 * Falls back to a `CODE 1.234,00` form if the browser rejects the currency code,
 * so a bad code degrades to a still-honest label rather than throwing inside a
 * table cell.
 */
function formatNativeTotal(value, currency) {
  const cur = String(currency || '').toUpperCase();
  try {
    return formatMoney(value, { currency: cur });
  } catch {
    return `${cur} ${formatNumber(value)}`;
  }
}

/** Der Betrag einer Bestellung — EINE Quelle fuer Zeile UND Karte (§2.6).
 *
 *  🔴 **Der Anlass ist die Zusicherung der G2-Karte: „Betraege auf Karte und
 *  Zeile textgleich".** Die Mobilkarte rundete bis G2b —
 *  `formatMoney(n, { digits: 0 })` —, die Zeile nicht; die Zusicherung war damit
 *  **verletzt**, und zwar sichtbar (`1.235 €` gegen `1.234,56 €`).
 *
 *  🔴 **Zwei Aufrufe mit demselben Ausdruck waeren zwei Quellen.** Der Ausdruck
 *  steht deshalb EINMAL hier, und beide Flaechen lesen ihn. Wer die Karte
 *  umbaut, kann die Zeile nicht mehr von ihr wegziehen — genau die Bauart, die
 *  eine Zusicherung ueber zwei Flaechen tragen kann.
 *
 *  Nicht umgerechnete Zeilen nennen ihren EIGENEN Waehrungscode, statt ein
 *  Euro-Zeichen zu behaupten, das sie nicht verdient haben. Der Wert ist
 *  unveraendert — nur die Einheit wird ehrlich benannt.
 *
 *  ⚠️ Der letzte Zweig ist in Produktion unerreichbar, und zwar aus ZWEI
 *  unabhaengigen Gruenden: der Listen-Serialisierer liefert immer ein endliches
 *  `total`, UND diese Wache laesst nur `undefined` durch (`Number(null) === 0`),
 *  was die Abbildung nicht erzeugen kann. Toter Kode, keine fehlende Sicherung.
 */
function betragText(po) {
  const eur = Number(po.totalEur);
  if (eur != null && isFinite(eur)) {
    return po.totalIsNative ? formatNativeTotal(eur, po.currency) : formatMoney(eur);
  }
  return po.invoice_total_normalized != null
    ? formatMoney(po.invoice_total_normalized)
    : '–';
}

const ROWS_PER_PAGE = 50;

// 🔴 EIN Statusvokabular, aus `POStatusBar` (Konzept §7.1e).
// Hier standen zwei eigene Listen. `BASE_STATUS_OPTIONS` bot „partially paid“
// an — einen Wert mit GENAU EINEM Treffer repoweit (diese Zeile selbst), den
// keine Bestellung tragen kann, sodass die Auswahl immer im Leerzustand endete
// — und „Done“ fehlte, obwohl `STATUS_ORDER` sechs Zustaende kennt: eine
// Bestellung in `Done` liess sich nicht filtern. `PO_STATUS_PILLS` fuehrte
// dieselben fuenf Zustaende ein drittes Mal in Kleinschreibung.
const STATUS_FILTER_OPTIONS = PO_STATUSES.map((value) => ({
  value,
  label: PO_STATUS_LABEL[value] || value,
}));
const DEFAULT_STATUS_VALUES = STATUS_FILTER_OPTIONS
  .filter((opt) => opt.value !== 'Closed')
  .map((opt) => opt.value);

// 🔴 Die sieben WERTE der Status-Reihe (§2.4): `PO_STATUSES` (sechs) PLUS
// `Cancelled`. Der Wert kommt an (die Zustandsmaschine laesst ihn aus jedem
// aktiven Zustand zu, `transitions.py:9/:45`) und stand in keiner Liste — genau
// die Defektklasse, die diese Karte behebt (§2.5).
const STATUS_VALUES = [...PO_STATUSES, 'Cancelled'];

// 🔴 Die EINE Vorgabekonstante (§2.4/BL-2): „alles ausser `Closed`" — SECHS
// Werte, `Cancelled` eingeschlossen. Sie wird von der Dimensions-Deklaration
// UND vom Hybrid-Praedikat gelesen; `DEFAULT_STATUS_VALUES` (fuenf) bleibt als
// Ableitung stehen, ist aber NICHT mehr die Vorgabe der Dimension.
const OPEN_STATUS_VALUES = [...DEFAULT_STATUS_VALUES, 'Cancelled'];

/** Die Sortier-Achse: die Felder der Tabellenkoepfe (N10.2, `sort`/`-sort`). */
const SORT_VALUES = [
  'po_id', 'vendorLabel', 'order_date', 'expected_arrival', 'totalEur', 'status', 'itemCount',
];

/** Die Anzeigenamen der Sortierfelder — EINE Quelle fuer Blatt und Kopf. */
const SORT_LABEL = {
  po_id: 'PO ID',
  vendorLabel: 'Vendor',
  order_date: 'Order Date',
  expected_arrival: 'Expected',
  totalEur: 'Total',
  status: 'Status',
  itemCount: '# Items',
};

/** Wo die Suche sucht — der `searchedIn`-Satz des Leerzustands (§2.8). */
const SUCHE_IN = 'PO ID, vendor, status';

/** Das Breitenmuster dieser Seite — EINE Quelle fuer Deckel und Meldung (§2.6).
 *
 *  🔴 `full`: die Seite rendert `<PageContainer width="full">`. Die Konstante
 *  steht hier, weil die zwei Verbraucher (`PageContainer` und der
 *  Rueckfallwert unten) NICHT denselben Kontext sehen: `PageContainer` setzt den
 *  `PageWidthContext` erst um seine KINDER, und `POOverview` ist sein ELTERNTEIL
 *  — ein `useContext(PageWidthContext)` hier laese den aeusseren Wert
 *  (`'reading'`), nicht den eigenen. Zwei Quellen fuer dasselbe Muster waeren
 *  genau die Doppelquelle, gegen die G0b.2 angetreten ist; deshalb EIN Literal
 *  an EINER Stelle, und `POOverviewSkeleton` traegt denselben Wert (PW14
 *  vergleicht beide Dateien). */
const MUSTER = 'full';

/** Die sieben Datenspalten in DOM-Reihenfolge (§2.6).
 *
 *  `priority`/`kind` sind die Eingaben von `useColumnPriority` (welche Spalte
 *  bei welcher Containerbreite weicht) und `COLUMN_MAX` (Ober-/Untergrenze der
 *  Zelle). 🔴 **Kein eigenes `max` am Deskriptor** — der Baustein leitet
 *  `min/max` aus `COLUMN_MAX[kind]` ab (Grill W12), zwei Quellen fuer dieselbe
 *  Zahl sind der Zustand, den das Konzept abschafft.
 *
 *  🔴 **Startwerte, nicht gemessen** (§3.2.2): G0b hat gemessen; weicht die
 *  Messung ab, folgt die Zahl der Messung. `kern` = immer sichtbar (auch im
 *  Rollfall), `lg` = ab 1024 Containerbreite. Die ⋮-Spalte steht NICHT hier —
 *  sie traegt keine Art, sondern `BEDIENBREITE.aktion` (§2.6). */
const SPALTEN = [
  { key: 'po_id', priority: 'kern', kind: 'identity' },
  { key: 'vendorLabel', priority: 'lg', kind: 'text' },
  { key: 'order_date', priority: 'lg', kind: 'date' },
  { key: 'expected_arrival', priority: 'kern', kind: 'date' },
  { key: 'totalEur', priority: 'kern', kind: 'number' },
  { key: 'status', priority: 'kern', kind: 'chip' },
  { key: 'itemCount', priority: 'lg', kind: 'number' },
];

/** `SPALTEN` nach Schluessel — fuer die Breite einer einzelnen Kopf-/Datenzelle. */
const SPALTE_VON = Object.fromEntries(SPALTEN.map((s) => [s.key, s]));

/** Der Titel der mobilen Karte (G2b) — die Kennung, und der EINZIGE Weg hinein.
 *
 *  🔴 N11.5: eine Kennung wird **nie gekuerzt** — `nowrap`, kein
 *  `text-overflow: ellipsis`, kein `overflow: hidden`. 🔴 Die Zwei-Zeilen-Klemme
 *  der Namenskarten (`ProductsTable.TITEL_KLEMME_SX`) wird hier **nicht**
 *  gesetzt: eine Bestellnummer ist kurz und bricht nie um, eine Klemme an einem
 *  `nowrap`-Text waere eine Zusicherung ohne Wirkung (Regel 15 §„nicht mehr
 *  scheitern"). Genau deshalb traegt P15 auf `/overview` zwei der drei
 *  Zusicherungen von `/products` und die dritte entfaellt **mit Grund**.
 *
 *  🔴 Der 44-px-Boden (N11.19, Regel 22 §8) liegt als FLAECHE (`::after`) um den
 *  Kasten, nicht als `minHeight`: ein `minHeight` am `Link` hoehe die Titelzeile
 *  — und damit jede Karte — um 44 − 21 = 23 px. Dieselbe Bauart wie
 *  `rma/RMAFaelligkeit.jsx:498-508` und `components/ProductsTable.jsx:365-395`.
 *  Die Zahl kommt aus dem Token, kein zweites Literal daneben.
 *
 *  🔴 **`display: 'block'` ist Teil der zitierten Hausform** (R16 W5, Runde 1):
 *  `ProductsTable.KARTE_TITEL_SX` traegt es mit ausfuehrlicher Begruendung
 *  (`:341-360`) — die Block-Ebene gibt dem `::after` genau EINEN Kasten als
 *  Containing Block. Hier war es weggelassen; `whiteSpace: 'nowrap'` erzwingt
 *  zwar genau eine Zeilenbox, aber die Abweichung von der zitierten Form war
 *  unbenannt, und der naechste Umbau liest den Verweis als Deckungsgleichheit.
 *  Deshalb steht sie jetzt da: gleiche Form, gleiche Geometrie.
 *
 *  🔴 **`export`, damit die Probe die Boden-Regel LESEN kann** (Hausform
 *  `ProductsTable.KARTE_TITEL_SX`, `ProductsTable.g1b-trefferflaeche.test.jsx`):
 *  jsdom wirft den `@media (pointer: coarse)`-Mantel um ein `::after` weg — aus
 *  dem gerenderten CSS ist die ZEIGER-Bedingung nicht lesbar, aus der Konstante
 *  schon. Ohne den Export war der 44-px-Boden auf `/overview` von keiner Probe
 *  gedeckt (R16 W3).
 *
 *  🔴 **Der Traeger ist MUI's `Link` (`MuiLink`), nicht der Router-`Link`** —
 *  und das ist keine Geschmacksfrage: `KARTE_TITEL_SX` traegt einen
 *  `@media (pointer: coarse)`-Block, und ein `style`-Attribut kann keine
 *  Media-Query aufnehmen (React schreibt die Schluessel als CSS-Eigenschaften
 *  in den Inline-Stil, der Block fiele still weg — der 44-px-Boden waere nur
 *  noch im Kommentar wahr). Der Weg hinein bleibt derselbe:
 *  `component={Link}` mit `to` (Hausform `rma/RMAFaelligkeit.jsx:477-479`). */
export const KARTE_TITEL_SX = {
  ...KARTE_TITEL_BASIS_SX,
  // Die Abweichung dieser Seite: eine Kennung („PO-2026-…") ist EINE Zeile.
  // Fläche und 44-px-Boden stehen seit R16-W4 (2026-10-21) EINMAL in
  // `MobileRecordCard.KARTE_TITEL_BASIS_SX` — vorher trugen drei Seiten
  // dieselbe Kopie, und `VendorsOverview` war die dritte.
  whiteSpace: 'nowrap',
};

export default function POOverview() {
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const { setHeaderConfig } = useHeaderConfig()
  const theme = useTheme()
  // 🔴 **Die Kartenweiche liegt bei `md` (< 900 px), nicht bei `sm` (< 600 px)**
  // (R16 W1, Nachbesserung 2). Hauspräzedenz ist `secondLife/OpenCases.jsx`:
  // eine breite Tabelle in einem schmalen Kasten ist kein Tablet-Layout,
  // sondern eine Tabelle, deren halbe Spalten hinter dem Scroll liegen.
  // Gemessen (R16 W1): bei 768 px (iPad hochkant) ist der Behälter 408 px —
  // PO ID + Expected + Total füllen ihn genau, **Status und ⋮ lägen
  // vollständig außerhalb des Sichtfelds**, und die Karte griff erst unter
  // 600 px.
  //
  // 🔴 Der Restbereich **900 – ~1030 px** bleibt TABELLE und rollt: dort ist
  // der Behälter 540–667 px, also unter der Rollschwelle 668
  // (`spaltenMinBreite`), und der Rollfall hat seinen Gegenstand (erste Spalte
  // klebt, Kantenhinweis). Ab ~1030 px ist der Behälter ≥ 668, und die Tabelle
  // passt ohne Rollen. Darunter trägt die Karte alles, was sonst hinter dem
  // Scroll läge.
  //
  // Der Name sagt die FOLGE, nicht das Gerät: „mobil" wäre bei 899 px falsch.
  // 🔴 R16 N1 (Nachbesserung 3): `{ noSsr: true }` ist die zitierte Hausform
  // (`pages/secondLife/OpenCases.jsx:391`) — ohne es liefert der ERSTE Render
  // `defaultMatches = false` und MUI zeichnet die Tabelle, bevor die
  // Medienabfrage greift. Heute folgenlos, weil `loading` mit `true` startet
  // und der Erstaufbau das Gerüst zeigt; die Option hält die Weiche trotzdem
  // deckungsgleich mit der Hausform, statt sich auf diese Reihenfolge zu
  // verlassen.
  const karteStattTabelle = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })

  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [products, setProducts] = useState([]);
  // O21 — der Abrufzustand als eigener Zustand. `products.length === 0`
  // heisst gleichzeitig "laedt", "leer" und "gescheitert", weil der
  // `.catch` unten nur protokolliert.
  const [produktLage, setProduktLage] = useState('laedt');

  // 🔴 Dasselbe fuer die Empfehlungen — aus demselben Grund wie O21, nur
  // dringender geworden: bis zum 2026-09-20 war der Abschnitt zugeklappt, also
  // sah niemand, dass `recommendedOrders.length === 0` gleichzeitig "laedt",
  // "leer" und "gescheitert" heisst (`.catch(() => {})` unten, stumm).
  //
  // Seit die Kachel "Urgent reorders" hierher verlinkt und der Abschnitt dabei
  // AUFKLAPPT, ist dieser Zustand die LANDUNG dieses Klicks: der Bediener liest
  // "12" und darunter "No recommended orders." -- waehrend der Abruf laeuft, und
  // dauerhaft, wenn er scheitert. Genau der Widerspruch zwischen Zahl und Liste,
  // den dieser Zweig beseitigen soll.
  const [empfehlungsLage, setEmpfehlungsLage] = useState('laedt');
  const [recommendedOrders, setRecommendedOrders] = useState([])
  const [vendorMap, setVendorMap] = useState({})

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // 🔴 R16 W2 (Nachbesserung 1, 2026-10-14): das Geruest gehoert dem ERSTEN
  // Aufbau, nicht jedem Auffrischen. §8.2 („Neuladen, Filter- oder Suchwechsel")
  // sagt drei Dinge zu: „die alten Zeilen bleiben, `aria-busy` am Daten-Panel;
  // `RefreshButton` dreht; der Zaehler springt nicht auf 0." Ohne diesen Zustand
  // demontierte `loading ? <Skelett/> : …` die ganze Flaeche samt Knopf — der
  // mitgegebene `loading`-Prop des Knopfes war beim Rendern IMMER `false`.
  // 🔴 Der Ausdruck ist ein „nie geladen"-Wert, NICHT `length === 0`
  // (`RefreshButton.firstLoad` nennt genau diesen Unterschied): das Auffrischen
  // einer legitim leeren Liste ist ein Auffrischen.
  const [jeGeladen, setJeGeladen] = useState(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeGroup, setActiveGroup] = useState(null)
  const [declinedDrawerOpen, setDeclinedDrawerOpen] = useState(false)

  const [vendorPerf, setVendorPerf] = useState({}); // { [vendor_id]: perfData }

  // ── Der Zustand der Liste lebt in der ADRESSE (§3.1/N10.1) ───────────────
  // Vorher lag er in fuenf `useState` (Suche, Status, Lieferant, Sortierung,
  // Seite): nicht teilbar, beim Neuladen verloren. `useListQuery` ist jetzt der
  // EINZIGE Schreiber der fuenf Parameter `q`/`status`/`vendor`/`sort`/`page`
  // (§3.3) — die Ansicht schreibt sie nicht zusaetzlich.
  // 🔴 `section` gehoert NICHT dazu: der Parameter ist fuer den Hook fremd und
  // ueberlebt jede Abfrageaenderung (§3.2, „Unrelated URL parameters are
  // PRESERVED"). Der Hook beruehrt ihn nicht.
  const dimensionen = useMemo(() => [
    // 🔴 `single` OHNE `values` — nur so entsteht der Chip „Search: <Begriff>"
    // in Z3 (N4.1/N7.10); einen zweiten Kanal gibt es nicht.
    { key: 'q', kind: 'single', label: 'Search' },
    {
      key: 'status',
      kind: 'multi',
      label: 'Status',
      default: OPEN_STATUS_VALUES,
      values: STATUS_VALUES,
    },
    { key: 'vendor', kind: 'multi', label: 'Vendor' },
  ], [])

  const query = useListQuery({
    dimensions: dimensionen,
    sort: { default: '-order_date', values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE,
  })
  const { state, set, clearAll } = query

  // Die Sortier-Achse traegt `feld` / `-feld` (N10.2); die Tabelle liest daraus
  // Feld und Richtung.
  const sortFeld = state.sort ?? '-order_date'
  const sortBy = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld
  const sortOrder = sortFeld.startsWith('-') ? 'desc' : 'asc'
  // `state.page` ist 1-basiert (N10.4), MUI's Seitenwechsel 0-basiert.
  const seite = state.page - 1

  React.useEffect(() => {
    setHeaderConfig({
      title: 'Purchase Orders',
      showSearch: true,
      actions: [],
    })
  }, [setHeaderConfig])

  const ladeEmpfehlungen = React.useCallback(() => {
    setEmpfehlungsLage('laedt')
    axios.get(`${API}/api/recommended_purchase_orders`)
      .then(r => { setRecommendedOrders(r.data || []); setEmpfehlungsLage('da') })
      .catch(() => setEmpfehlungsLage('fehler'))   // gescheitert ist nicht leer
  }, [])

  // Der PO-Abruf liegt in einem `useCallback`, weil der Fehlerzustand einen
  // Wiederholen-Knopf traegt (§Fehler != leer). Ein Fehlschlag ist NICHT
  // "noch keine Bestellungen" — die Liste ist unbekannt, nicht leer.
  const ladePurchaseOrders = React.useCallback(() => {
    setLoading(true)
    setError('')
    axios.get(`${API}/api/purchase_orders`)
      .then(r => { setPurchaseOrders(r.data || []); setJeGeladen(true) })
      .catch(() => setError('Failed loading POs.'))
      // 🔴 `jeGeladen` heisst „der Erstaufbau ist GELUNGEN" (R15r3 B1/R16r3 B1):
      // es steht im `then`, NICHT im `finally` — dort wurde es auch beim
      // GESCHEITERTEN Erstaufbau gesetzt, und `sperre = error && !jeGeladen` war
      // damit ein konstanter Ausdruck (Sperre, Grund-Alert, Zaehler-Gatter tot).
      .finally(() => setLoading(false))
  }, [])

  // load data
  useEffect(() => {
    ladePurchaseOrders()

    ladeEmpfehlungen()

    axios.get(`${API}/api/vendors`)
      .then(r => {
        const map = Object.fromEntries(r.data.map(v => [String(v.id), v]))
        setVendorMap(map)
      })
      .catch(() => {})
    // 🔴 `ladeEmpfehlungen` und `ladePurchaseOrders` sind `useCallback(..., [])`
    //    und damit ueber die Lebensdauer der Komponente identitaetsstabil. Sie in
    //    die Liste aufzunehmen kann deshalb KEINE Schleife ausloesen -- der Effekt
    //    laeuft weiterhin genau einmal. Ohne die Eintraege scheitert der
    //    Produktionsbau: CRA behandelt Warnungen als Fehler, und
    //    `react-hooks/exhaustive-deps` ist eine.
  }, [ladeEmpfehlungen, ladePurchaseOrders])

  useEffect(() => {
    axios.get(`${API}/api/products`)
      .then(r => { setProducts(r.data || []); setProduktLage('da'); })
      .catch(() => {
        console.error('Failed loading products.');
        setProduktLage('fehler');   // O21: gescheitert ist nicht leer
      });
  }, []);

  // 🔴 Der Seiten-Reset bei jeder Abfrageaenderung ist entfallen: `useListQuery`
  // setzt die Seite selbst auf 1 (N10.3, `useListQuery.js` `set`, „if (key !==
  // 'page') naechste.delete('page')"). Ein zweiter Reset hier waere die zweite
  // Quelle fuer dieselbe Regel.

  useEffect(() => {
    if (!purchaseOrders.length) return;
    const inDeliveryVendorIds = [...new Set(
      purchaseOrders
        .filter(p => p.status === 'In Delivery' && p.vendor_id)
        .map(p => p.vendor_id)
    )];
    if (inDeliveryVendorIds.length === 0) return;
    Promise.all(
      inDeliveryVendorIds.map(vid =>
        axios.get(`${API}/api/purchase_orders/vendor_performance?vendor_id=${vid}`)
          .then(r => ({ vendor_id: vid, data: r.data }))
          .catch(() => null)
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { if (r) map[r.vendor_id] = r.data; });
      setVendorPerf(map);
    });
  }, [purchaseOrders]);

  // preview drawer
  const handlePreview = group => {
    const vd = vendorMap[group.vendor_id] || {}
    setActiveGroup({ ...group, vendor_details: vd })
    setDrawerOpen(true)
  }
  const handleAccept = (vid, orders) => {
    setRecommendedOrders(rs => rs.filter(g => g.vendor_id !== vid))
    nav('/new', { state: { vendor_id: vid, recommended_orders: orders } })
  }
  
  const handleDecline = (vendorId, items) => {
    // items: array of { sku, ... } for per-product decline
    axios.post(`${API}/api/recommended_purchase_orders/decline`, {
      vendor_id: vendorId,
      recommended_orders: items
    }).then(() => {
      setRecommendedOrders(prev => {
        return prev
          .map(group => {
            if (group.vendor_id !== vendorId) return group;

            // If no items passed OR we passed exactly all items → drop the group
            const allSkus = new Set((group.recommended_orders || []).map(i => i.sku));
            const declinedSkus = new Set((items || []).map(i => i.sku));
            const isDeclineAll =
              !items || items.length === 0 || declinedSkus.size === allSkus.size;

            if (isDeclineAll) return null; // remove whole vendor recommendation

            // Otherwise remove only declined items
            const remaining = (group.recommended_orders || []).filter(
              it => !declinedSkus.has(it.sku)
            );
            // If nothing remains, drop the group
            if (remaining.length === 0) return null;
            return { ...group, recommended_orders: remaining };
          })
          .filter(Boolean);
      });
    }).catch(err => {
      console.error('Decline failed', err);
      // (optional) surface an Alert state here
    });
  }

  // Auslauf aus dem Empfehlungs-Drawer (2026-09-14).
  //
  // Kein zusaetzliches „Decline": die beiden sind verschiedene Dinge. Ein
  // Decline haelt 14 Tage, ein Auslauf gilt dauerhaft — und
  // `GET /api/recommended_purchase_orders` filtert `discontinued_at <= heute`
  // ohnehin bei jedem Abruf (`routes.py:2103`). Es ist also nichts
  // nachzutragen, nur der lokale Zustand nachzuziehen.
  const handleDiscontinued = (vendorId, sku, antwort) => {
    if (!zeileFaelltAusDenEmpfehlungen(antwort?.discontinued_at)) {
      // Ankuendigung mit Vorlauf: die Zeile bleibt bestellbar. Nur den Zustand
      // an der Zeile nachtragen, damit ein erneutes Oeffnen richtig vorbelegt.
      setRecommendedOrders(prev => prev.map(group => (
        group.vendor_id !== vendorId ? group : {
          ...group,
          recommended_orders: (group.recommended_orders || []).map(it => (
            it.sku === sku
              ? {
                  ...it,
                  discontinued_at: antwort.discontinued_at,
                  discontinued_note: antwort.discontinued_note,
                }
              : it
          )),
        }
      )))
      return
    }

    // Wirkt sofort: SKU raus, leere Lieferantengruppe faellt weg — dieselbe
    // Form wie in `handleDecline`.
    setRecommendedOrders(prev => prev
      .map(group => {
        if (group.vendor_id !== vendorId) return group
        const remaining = (group.recommended_orders || []).filter(it => it.sku !== sku)
        if (remaining.length === 0) return null
        return { ...group, recommended_orders: remaining }
      })
      .filter(Boolean))
  }

  // sort handler — schreibt die Sortier-ACHSE (N10.2: `feld` / `-feld`)
  const handleSort = (field) => {
    if (!field) return;
    if (sortBy === field) {
      set('sort', sortOrder === 'asc' ? `-${field}` : field);
    } else {
      set('sort', field === 'order_date' ? `-${field}` : field);
    }
  };

  // date formatter
  const fmtDate = iso => formatDate(iso, { placeholder: '–' })

  const enrichedOrders = useMemo(() => {
    return purchaseOrders
      .filter((po) => !po.is_deleted)
      .map((po) => {
        const vendorKey = String(po.vendor_id ?? 'unknown');
        const vendorDetails = vendorMap[vendorKey];
        const vendorLabel =
          po.vendor_metaobject_name ||
          vendorDetails?.metaobject_name ||
          vendorDetails?.vendor_name ||
          'Unknown vendor';

        const orderItems = Array.isArray(po.order_items) ? po.order_items : [];
        // Kanonischer Schluessel statt Kleinschreibung: `ToDo` (der Vorgabewert
        // der Spalte, `backend/models.py:725`) wird zu `Draft`, sonst stuende er
        // als roher Token in der Statusspalte und in keiner Filterliste.
        const statusNormalized = poStatusKey((po.status || '').trim());
        const totalUnits = orderItems.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0
        );
        const acceptedUnits = orderItems.reduce(
          (sum, item) => sum + (Number(item.accepted_quantity) || 0),
          0
        );

        let bookingLabel = 'None booked';
        let bookingKey = 0;
        if (totalUnits > 0 && acceptedUnits === totalUnits) {
          bookingLabel = 'Fully booked';
          bookingKey = 2;
        } else if (acceptedUnits > 0) {
          bookingLabel = 'Partially booked';
          bookingKey = 1;
        }

        const totalEur = euroTotalForPO(po);
        // 🔴 Rule 16 blocker: this figure is NOT euro on every row. Where the PO
        // carries no usable conversion_rate (18 rows on prod today), it is the
        // native amount, unconverted. The number and the sort key are untouched;
        // the row just stops pretending the cell is euro when it is not.
        const totalIsNative = isUnconvertedNative(po);

        return {
          ...po,
          vendorKey,
          vendorLabel,
          statusNormalized,
          bookingLabel,
          bookingKey,
          totalEur,
          totalIsNative,
          itemCount: orderItems.length,
          // 🔴 „Responsible“ ist raus (Konzept §7.1a / Posten #10). Die Spalte war
          // auf allen 15 gemessenen Zeilen leer, UND der Suchhinweis versprach
          // „Search PO ID, vendor, responsible“ — eine Suchdimension, die nichts
          // traf. Der Listen-Endpunkt liefert `responsible_id`/`responsible`
          // sehr wohl (der Kopfvermerk zu §1.2 hat den alten Zeiger widerlegt);
          // leer sind die DATEN. Ob die Spalte zurueckkommt, ist eine
          // Eigentuemerfrage — bis dahin verspricht die Oberflaeche nichts.
          searchBlob: `${po.po_id} ${vendorLabel} ${PO_STATUS_LABEL[statusNormalized] || po.status || ''}`.toLowerCase(),
        };
      });
  }, [purchaseOrders, vendorMap]);

  const vendorOptions = useMemo(() => {
    const map = new Map();
    enrichedOrders.forEach((po) => {
      if (!map.has(po.vendorKey)) {
        map.set(po.vendorKey, po.vendorLabel || 'Unknown vendor');
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [enrichedOrders]);

  // 🔴 EIN Mengen-Praedikat, EINE Quelle (Grill WV3, Bedingung 1 + W2).
  // `statusSet` wird GENAU EINMAL definiert und von BEIDEN Verbrauchern gelesen:
  // dem Hybrid-Zweig in `filteredRows` und der ` · default`-Beschriftung des
  // Chips. `chip.isDefault` wird NIRGENDS gelesen — es ist order-sensitiv
  // (`gleich` vergleicht indexweise, `useListQuery.js:439-443`): dieselbe MENGE
  // in anderer Reihenfolge gilt dort als „geaendert" (Wiedervorlage 2, BL-3).
  const statusSet = useMemo(() => new Set(state.status), [state.status]);
  const isStatusDefault = useMemo(
    () => state.status.length === OPEN_STATUS_VALUES.length
      && OPEN_STATUS_VALUES.every((value) => statusSet.has(value)),
    [state.status, statusSet],
  );

  const hasActiveFilters = Boolean(
    (state.q ?? '').trim() || state.vendor.length || !isStatusDefault
  );

  // 🔴 „Nur die Suche engt ein" — der Unterscheider der ZWEI no-match-Wortlaute
  // (§2.8). Die Vorgabe zaehlt hier NICHT als Einengung: sie ist benannt und
  // steht als Chip in Z3.
  const nurSuche = Boolean((state.q ?? '').trim())
    && state.vendor.length === 0
    && isStatusDefault;

  const filteredRows = useMemo(() => {
    const searchValue = (state.q ?? '').trim().toLowerCase();
    const vendorSet = new Set(state.vendor);

    return enrichedOrders.filter((po) => {
      // 🔴 HYBRID (Grill B1 / Bedingung 5): unter der Vorgabe „≠ Closed", sonst
      // Mitgliedschaft. Die reine Mitgliedschaft waere unter der Vorgabe NICHT
      // aequivalent — ein Status AUSSERHALB der sieben liesse „≠ Closed" durch,
      // die Menge verwuerfe ihn (stiller Verlust, Regel 20 §1).
      if (isStatusDefault) {
        if (po.statusNormalized === 'Closed') return false;
      } else if (statusSet.size && !statusSet.has(po.statusNormalized)) {
        return false;
      }

      if (vendorSet.size && !vendorSet.has(po.vendorKey)) return false;
      if (searchValue && !po.searchBlob.includes(searchValue)) return false;
      return true;
    });
  }, [enrichedOrders, state.q, state.vendor, statusSet, isStatusDefault]);

  // 🔴 Bauform 2 (Grill WV3, Bedingung 2): die Ansicht ERSETZT die Beschriftung
  // des `status`-Chips — sie haengt NICHT an. `baueChips` haengt sonst bereits
  // ` · default` an (`useListQuery.js:686`), und im kanonischen Fall entstuende
  // ein doppeltes Suffix. Alle uebrigen Chips und jedes `onRemove` bleiben
  // byte-gleich; `ActiveFilterBar` liest `isDefault` im Rendern nicht.
  // 🔴 Das Gate ist DIESES Praedikat, nicht `chip.isDefault` — sonst fiele das
  // Suffix im Umordnungsfall weg (das zweite BL-3-Symptom).
  const chips = useMemo(() => query.chips.map((chip) => (
    chip.id === 'status' && isStatusDefault
      ? { ...chip, label: 'Status: Open · default' }
      : chip
  )), [query.chips, isStatusDefault]);

  // N8.5/P9: der Fehler des ERSTEN Aufbaus sperrt die Bedienelemente der Leiste
  // (Suche, Facette, Pillenreihe, Filter-Oeffner); der Grund steht sichtbar
  // darueber.
  // 🔴 R15r2 W3 (Nachbesserung 2) + R15r3 B1/R16r3 B1 (Nachbesserung 3): gesperrt
  // wird NUR der GESCHEITERTE ERSTE Aufbau — `jeGeladen` faellt erst im `then`,
  // nicht mehr im `finally`. Scheitert ein AUFFRISCHEN nach erfolgreichem
  // Erstaufbau, stehen die zuletzt geladenen Zeilen noch — die Bedienelemente
  // bleiben bedienbar, und ein eigener Hinweis nennt den Fehler UND den Weg
  // zurueck. Vorher sperrte `Boolean(error)` auch diese Lage und zeigte
  // „… locked until the list loads", obwohl die Liste geladen hatte.
  const sperre = Boolean(error) && !jeGeladen;

  // Der Zaehler je Statuswert ueber den BESTAND (§4) — die Quelle liegt
  // vollstaendig vor (der Endpunkt kappt nicht). 🔴 Ein gemessener `0` wird als
  // `0` gezeigt, nie weggelassen (§2.5/W8): „fehlt sie, steht keine Zahl" meint
  // die FEHLENDE Zahl (die Quelle antwortet nicht), nicht eine gemessene Null.
  const statusZaehler = useMemo(() => {
    const zaehler = {};
    enrichedOrders.forEach((po) => {
      zaehler[po.statusNormalized] = (zaehler[po.statusNormalized] || 0) + 1;
    });
    return zaehler;
  }, [enrichedOrders]);

  const statusOptionen = useMemo(
    () => STATUS_VALUES.map((value) => ({
      value,
      label: PO_STATUS_LABEL[value] || value,
      count: statusZaehler[value] ?? 0,
    })),
    [statusZaehler],
  );

  const leerGrund = error ? 'error' : (enrichedOrders.length ? 'no-match' : 'no-data');

  // 🔴 R16 W2: „Geruest nur beim ersten Aufbau". `loading` bleibt beim
  // Auffrischen wahr — genau darum dreht der `RefreshButton` dann (er liest
  // denselben Wert), das Panel traegt `aria-busy`, und die alten Zeilen stehen.
  // 🔴 „Erstaufbau" heisst seit der Nachbesserung 3 „noch NIE ERFOLGREICH geladen"
  // (`jeGeladen` faellt im `then`): ein „Try again" nach gescheitertem Erstaufbau
  // traegt damit wieder das Geruest. Das ist §8.2 („das Geruest gehoert dem ersten
  // Aufbau"), kein Rueckfall — und es ist genau der Wert, den R16 W2 verlangt.
  const ersteLadung = loading && !jeGeladen;

  // 🔴 R16r2 (Nachbesserung 2, 2026-10-14) / Konzept §8.1, dritte Zeile: „ist
  // eine Vorgabe BETEILIGT, wird sie genannt („Resolved cases are hidden.")" und
  // „bei Vorgabe zusätzlich die Vorgabe als Knopf". 🔴 Der Ausloeser ist
  // „beteiligt" (sie filtert), NICHT „sie ist die einzige Einengung" — bis zur
  // Nachbesserung 2 stand hier `&& !nurSuche && !hasActiveFilters`, und damit
  // fehlte der Vorgabe-Hinweis genau dann, wenn Suche oder Facette MIT der
  // Vorgabe zusammen nichts trafen. Hausvorbild ist
  // `RMACasesDashboardBody.jsx` › `resolvedAll && onShowResolved` (bis G5a `hideClosed && …`): der Vorgabe-Ausweg
  // steht dort NEBEN dem allgemeinen „Clear filters", sobald beide zutreffen.
  const vorgabeBeteiligt = leerGrund === 'no-match' && isStatusDefault;

  // Der Ausweg der Vorgabe: die Vorgabe ABWAEHLEN (Wert wird „alle", §4.1) —
  // danach traegt `status` keinen Chip mehr, weil nichts mehr einengt. NICHT
  // „alle sieben Werte anwaehlen": das waere eine zweite Beschriftung derselben
  // Sache (`Status: Order placed, Paid, …`) statt des Grundzustands.
  const vorgabeZeigen = () => set('status', []);

  const neuLaden = () => {
    ladePurchaseOrders();
    ladeEmpfehlungen();
  };

  const sortedRows = useMemo(() => {
    const getValue = (row, field) => {
      switch (field) {
        case 'totalEur':
          // 🔴 Bei einer NICHT umgerechneten Zeile (`row.totalIsNative`) ist das
          // die Zahl in Fremdwaehrung, ungewichtet gegen die EUR-Zeilen daneben.
          // Bekannter Kompromiss — aber sein Wirkradius ist seit dem 2026-09-08
          // groesser: vorher traf er nur die Altzeilen mit unbrauchbarem Kurs
          // (`= 1.0`), jetzt JEDE Bestellung ausserhalb EUR/USD auf Dauer, auch
          // eine sauber angelegte (Owner-Entscheid O29, `poFxToEur.js`).
          // Wer das sortierbar machen will, braucht einen Umrechnungskurs — und
          // damit die Gegenprobe gegen den Tageskurs (EK-H1), nicht eine
          // Sortierheuristik hier.
          return Number.isFinite(row.totalEur) ? row.totalEur : -Infinity;
        case 'itemCount':
          return row.itemCount ?? 0;
        case 'bookingKey':
          return row.bookingKey ?? 0;
        case 'vendorLabel':
          return row.vendorLabel || '';
        default:
          return row[field] ?? '';
      }
    };

    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const direction = sortOrder === 'asc' ? 1 : -1;
      const av = getValue(a, sortBy);
      const bv = getValue(b, sortBy);

      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = av.toString().localeCompare(bv.toString(), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      return cmp * direction;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  // N10.4: die Seite wird gegen die Trefferzahl geklemmt — der Hook kennt die
  // Zeilenzahl nicht, die Ansicht schon.
  useEffect(() => {
    if (seite > 0 && seite * ROWS_PER_PAGE >= sortedRows.length) {
      set('page', 1);
    }
  }, [seite, sortedRows.length, set]);

  const paginatedRows = useMemo(
    () =>
      sortedRows.slice(
        seite * ROWS_PER_PAGE,
        seite * ROWS_PER_PAGE + ROWS_PER_PAGE
      ),
    [sortedRows, seite]
  );

  // ── Die Breitenmechanik (§2.6) ───────────────────────────────────────────
  // 🔴 Die MESSUNG sitzt am `liste-tabelle`-Traeger, nicht am `TableViewport`:
  // gemessen wird der RAUM (die rohe Innenbreite des Behaelters), nicht die
  // gedeckelte Tabelle — sonst maesse sich der Deckel selbst (BW4,
  // `ListenMuster.jsx:802-813`).
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  // 🔴 Der Rueckfall VOR der ersten Messung ist das Muster der Seite
  // (`CONTENT_WIDTH.full` ist `null` → `reading`), genau wie in
  // `TableViewport`/`ListenMuster` — nie 0 (Haus-Satz, §4.3:623-624).
  const messBasis = behaelterBreite ?? CONTENT_WIDTH[MUSTER] ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  const sichtbar = useMemo(
    () => new Set(sichtbareSpalten.map((s) => s.key)),
    [sichtbareSpalten]
  )
  // Das Spaltenbudget = Σ `COLUMN_MAX[kind].max` ueber die SICHTBAREN Spalten
  // (§4.1:567). `?? 0`: eine Spalte mit unbekannter Art hat kein `max`, und EIN
  // falscher Deskriptor darf nicht die ganze Tabelle reissen.
  const spaltenBudget = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0),
    0
  )
  // Die Tabellenbreite = min(Behaelter, Budget) (§4.1:568). `TableViewport`
  // MELDET diesen Wert nur (G0b.4) — das ANWENDEN ist Sache der Ansicht.
  const tabellenMax = Math.min(messBasis, spaltenBudget)
  // 🔴 Die ⋮-Spalte steht NEBEN dem Budget: §4.1:567 zaehlt nur Datenspalten,
  // und die Aktionsspalte traegt keine Art. Mit `tableLayout: 'fixed'` und
  // Datenspalten, die zusammen genau 100 % tragen, bekaeme sie 0 px — die
  // Zeilenaktion laege dann ueber der letzten Datenspalte (F1). Sie bekommt
  // deshalb `BEDIENBREITE.aktion` (140), und die Datenspalten teilen sich den
  // REST proportional zu ihrem `max` — die Summe bleibt 100 %, die Tabelle also
  // `tabellenMax` (PW4/PW4b unberuehrt). Dieselbe Bauform wie
  // `ListenMuster.jsx:315-334`; `/overview` hat KEINE Auswahlspalte (§13.1 gibt
  // G2 kein BS16), deshalb nur die Aktionshaelfte.
  const bedienSumme = BEDIENBREITE.aktion

  // 🔴 R15 B1 / R16 W4 (Nachbesserung 1): die Tabelle darf ihre Kernspalten
  // NIE unter `COLUMN_MAX[kind].min` quetschen. Unterhalb der Schwelle rollt
  // der `TableContainer` waagerecht — und erst DANN haben `stickyFirstColumn`
  // und `edgeHint` einen Gegenstand (bis G2b waren sie wirkungslos, weil die
  // Tabelle immer auf den Behaelter passte).
  //
  // Die Schwelle ist NICHT die Summe der Minima (596 fuer die vier Kernspalten
  // + ⋮), sondern die Breite, ab der die PROPORTIONALE Verteilung jede Spalte
  // ueber ihrem Minimum haelt: Breite_i = (max_i / Budget) × (Tabelle − 140).
  // Aus Breite_i ≥ min_i folgt T ≥ 140 + min_i × Budget / max_i; das Maximum
  // ueber die sichtbaren Spalten ist die Rollschwelle. Fuer die vier
  // Kernspalten: 140 + 96 × 880/160 = **668 px**. Zwischen 596 und 668 reichte
  // die Summe der Minima zwar, die proportionale Verteilung drueckte `Expected`
  // aber unter 96 px — deshalb rollen wir lieber (Auftrag: „lieber rollen als
  // quetschen").
  //
  // Die Schwelle wird JE SICHTBARER MENGE gerechnet: kommen optionale Spalten
  // dazu, waechst das Budget, und die Schwelle wandert mit (5 Spalten: 764,
  // 6: 860, 7: 1052). Sie bindet nur unterhalb des Fit-Bereichs — dort ist der
  // Behaelter ohnehin kleiner als das Budget.
  const spaltenMinBreite =
    bedienSumme +
    sichtbareSpalten.reduce((schwelle, sp) => {
      const { min, max } = COLUMN_MAX[sp.kind] ?? {}
      if (!min || !max || spaltenBudget <= 0) return schwelle
      return Math.max(schwelle, (min * spaltenBudget) / max)
    }, 0)

  // Die TATSAECHLICHE Tabellenbreite: nie unter der Rollschwelle. Liegt der
  // Behaelter darunter, bleibt die Tabelle auf `spaltenMinBreite` und der
  // `TableContainer` (`overflow: auto`) rollt. Sonst gilt der Deckel
  // min(Behaelter, Budget) unveraendert (PW4/PW4b).
  const tabellenIst = Math.max(tabellenMax, spaltenMinBreite)
  const datenAnteil = tabellenIst > bedienSumme ? (tabellenIst - bedienSumme) / tabellenIst : 0
  /** Die erklaerte Breite EINER Datenspalte — als Anteil des Daten-Rests.
   *
   *  🔴 Bei `tabellenIst === spaltenBudget` ist sie genau `COLUMN_MAX[kind].max`
   *  und darunter nie groesser (PW7: Zelle ≤ `max` + 1 px); unterhalb der
   *  Rollschwelle ist sie NIE kleiner als `COLUMN_MAX[kind].min` (R15 B1).
   *  Ohne Budget (leere Sichtbarkeit) bleibt sie leer — dann hat die Tabelle
   *  auch nichts zu zeigen. */
  const spaltenBreite = (key) => {
    if (spaltenBudget <= 0) return {}
    const max = COLUMN_MAX[SPALTE_VON[key]?.kind]?.max ?? 0
    return { width: `${(max / spaltenBudget) * datenAnteil * 100}%` }
  }

  const headerActions = [
    {
      key: 'new-po',
      label: 'New PO',
      startIcon: <AddIcon fontSize="small" />,
      component: Link,
      to: '/new',
    },
  ];

  // 🔴 „Show currently declined" wandert aus dem Seitenkopf in die Z1-Seiten-
  // aktionen (§2.7, Punkt V). Die Datenquelle und der Drawer bleiben unveraendert
  // — der Knopf zeigt ABGELEHNTE PRODUKTEMPFEHLUNGEN, nicht Bestellungen, ist
  // also weder eine Sicht auf eine Teilmenge der PO-Liste (kein Reiter) noch ein
  // Filter der Liste (kein Quick-Filter).
  const seitenAktionen = [
    {
      key: 'show-declined',
      label: 'Show currently declined',
      onClick: () => setDeclinedDrawerOpen(true),
    },
  ];

  // ── Die Bedienelemente der Leiste (§2.2/§2.3) ────────────────────────────
  // 🔴 Genau EINE Facette (Vendor): `ListToolbar` wirft bei mehr als drei, und
  // §2.3 nennt fuer G2 genau eine. Die STATUS-Reihe ist Z2 und steht in BEIDEN
  // Breiten inline (`FilterPillRow` gibt ohne `alwaysShow` nur am Desktop
  // `null` zurueck) — sie ist deshalb KEIN Blatt-Kriterium.
  const facetten = [
    <BottomSheetMultiSelect
      key="vendor"
      desktopForm="pill"
      label="Vendor"
      value={state.vendor}
      onChange={(v) => set('vendor', v)}
      options={vendorOptions}
      disabled={sperre}
    />,
  ];

  const statusReihe = (
    <FilterPillRow
      multi
      alwaysShow
      // 🔴 R16 B1 (Nachbesserung 1, 2026-10-14): OHNE Doppelpunkt. Der Baustein
      // ergaenzt ihn selbst (`FilterPillRow.jsx:377` rendert `{label}:`), und er
      // baut aus demselben Prop auch den Gruppennamen („Status: (any of)"). Mit
      // „Status:" stand im DOM „Status::" und im Namen „Status:: (any of)" — auf
      // jeder Ladung von `/overview`. Die Hausform ist der reine
      // Dimensionsname: der eigene Jest des Bausteins faehrt
      // `zeichneSchlicht({ label: 'Status' })` (`FilterPillRow.test.jsx:130-131`),
      // und das Konzept §3.2 schreibt „Reihe beginnt mit dem Dimensionsnamen
      // („Status:"), dann je Wert eine Pille" — EIN Doppelpunkt, vom Baustein.
      label="Status"
      options={statusOptionen}
      value={state.status}
      onChange={(v) => set('status', v)}
      disabled={sperre}
    />
  );

  // 🔴 Der Inhalt von `moreFilters.criteria` ist GENANNT, nicht offengelassen
  // (§2.3/W2): die Facetten stehen NUR ab `sm` inline
  // (`ListToolbar.jsx:294`), ohne Eintrag waere der Lieferantenfilter mobil
  // unerreichbar (stiller Verlust) und der „Filters"-Knopf (P16) bzw. das Blatt
  // (P17) haetten kein Kriterium.
  const blattKriterien = [
    {
      key: 'vendor',
      kind: 'select',
      label: 'Vendor',
      multiple: true,
      value: state.vendor,
      onChange: (v) => set('vendor', v),
      options: vendorOptions,
      // 🔴 N11.14: der BENANNTE Leerwert eines Kriteriums ohne Wert. „Any" ist
      // dort der WERT, keine Option — P25 verbietet „All…"/„Any" als
      // FacettenOPTION, nicht den angezeigten Blatt-Wert.
      emptyLabel: 'Any',
      disabled: sperre,
    },
  ];

  // N11.14: der Sortier-Abschnitt ist der ERSTE des Blattes. Auf dem Telefon
  // wandert die Sortierung hierher (N11.14/§11.4); am Desktop sitzt sie im
  // Tabellenkopf, und der `view`-Slot der Leiste bleibt leer (§2.1 5a).
  const blattSort = {
    value: sortFeld,
    onChange: (v) => set('sort', v),
    options: SORT_VALUES.flatMap((feld) => [
      { value: feld, label: `${SORT_LABEL[feld]} (ascending)` },
      { value: `-${feld}`, label: `${SORT_LABEL[feld]} (descending)` },
    ]),
  };

  // N4.6: die BLATT-Kriterien mit Wert, inklusive Vorgaben — NICHT
  // `query.chips.length` (das zaehlte die Suche mit, die kein Blatt-Kriterium
  // ist). Das Blatt fuehrt genau EIN Kriterium.
  const blattAktiv = state.vendor.length > 0 ? 1 : 0;

  // §2.8: der EINE Leerzustand der Ansicht (mobil wie Desktop dieselbe Flaeche).
  const leerzustand = (
    <EmptyState
      reason={leerGrund}
      {...(leerGrund === 'no-match' && nurSuche
        ? {
          query: (state.q ?? '').trim(),
          noun: 'purchase order',
          searchedIn: SUCHE_IN,
          onClearSearch: () => set('q', null),
        }
        : {})}
      message={
        leerGrund === 'error'
          ? 'Could not load purchase orders.'
          : leerGrund === 'no-data'
            ? 'No purchase orders yet.'
            : (nurSuche ? undefined : 'No purchase orders match these filters.')
      }
      // §8.1: „ist eine Vorgabe beteiligt, wird sie genannt („Resolved cases are
      // hidden.")". Der Wortlaut nennt die VORGABE, nicht den Mechanismus.
      detail={
        leerGrund === 'no-data'
          ? 'Create one with „New PO".'
          : vorgabeBeteiligt ? 'Closed purchase orders are hidden by default.' : undefined
      }
      // §8.1: „bei Vorgabe zusätzlich die Vorgabe als Knopf („Show resolved
      // cases")". Hausvorbild ist `RMACasesDashboardBody.jsx` › `resolvedAll && onShowResolved` — dort
      // stehen „Clear filters" (`outlined`) und „Show resolved cases" (`text`) in
      // EINER Reihe, sobald beide zutreffen. Genau so hier: der allgemeine Ausweg
      // steht NEBEN dem Vorgabe-Ausweg, nicht statt seiner. Ohne den eigenen
      // Knopf bliebe der ✕ des Vorgabe-Chips in Z3 der einzige Ausweg, und der
      // Leerzustand nennte ihn nicht.
      action={
        vorgabeBeteiligt
          ? (
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
              {/* Derselbe Riegel wie beim allgemeinen Ausweg unten: „Clear all
                  filters" erscheint nicht, wenn NUR die Suche engt — dort ist
                  „Clear search" der Ausweg, und zwei Knoepfe mit derselben Folge
                  waeren eine Doppelung. */}
              {!nurSuche && hasActiveFilters && (
                <Button variant="outlined" onClick={clearAll}>Clear all filters</Button>
              )}
              <Button variant="text" onClick={vorgabeZeigen}>Show closed</Button>
            </Stack>
          )
          : undefined
      }
      {...(leerGrund === 'error'
        ? { onRetry: ladePurchaseOrders }
        : (leerGrund === 'no-match' && !nurSuche && hasActiveFilters && !vorgabeBeteiligt
          ? { onClearFilters: clearAll }
          : {}))}
    />
  );

  return (
    <Fade in timeout={DUR.ui}>
      <Box>
        {/* 🔴 G2b (§2.6): `width="full"` — die erste `full`-Liste. Bis hierher
            trug die Seite die Vorgabe (`reading`, 1280), und sichtbar wurde das
            erst ab 1920. `MUSTER` oben und `POOverviewSkeleton` tragen denselben
            Wert; **PW14** vergleicht Skelett und Seite im Quelltext UND die
            gerenderte Blockbreite.
            🔴 R16 B1 (Nachbesserung 1): der KOPF steht INNERHALB des Containers
            (Hausform `ProductsOverview.jsx:53-61`, `tableWidth.jsx:117`). Vorher
            waren Kopf und Container GESCHWISTER: der Kopf las den
            Kontext-Vorgabewert (`reading`, 1280) und blieb zentriert, waehrend
            der Rumpf `full` wurde — ab 1920 fielen beide sichtbar auseinander
            (PW3 fuehrt genau diesen Zustand als KALIBRIERFALL). Im Container
            liest der Kopf `'full'`, haengt am `TableWidthProvider` und
            adoptiert die gemeldete Tabellenbreite. */}
        <PageContainer width={MUSTER} sx={{ pb: 6 }}>
          {/* ── Zone 1 — Kopf (§2.1). 🔴 Die Marke `liste-kopf` stempelt KEIN
              geteilter Baustein (am Kode gemessen, §5.3/B2): sie wird hier von
              Hand gesetzt, wie `liste-panel`/`liste-tabelle`/`liste-fuss`.
              `oeffne()` wartet auf `liste-panel`, P3 liest die Reihenfolge. */}
          <Box data-testid="liste-kopf">
            <ViewHeader
              title="Purchase Orders"
              breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Purchase Orders' }]}
              actions={headerActions}
            />
          </Box>
          <Stack spacing={3}>
            {/* R16-A N2: kein eigener Fehler-`Alert` mehr — der Fehler-`EmptyState` unten (`:755`/`:995`) trägt Text, Endpunkt und „Try again" und rendert bei `error` immer mit (`purchaseOrders` ist dann `[]`); Begründung in `docs/backlog-auto-repair/2026-10-04-pr3-abgelehnte-gate-warnungen.md` §7. */}

            {/* 🔴 Zwei Dinge, beide vom Regel-16-Gate am 2026-09-19 belegt.

                1. AUFGEKLAPPT, wenn der Bediener von der Kachel "Urgent reorders"
                   kommt (`UrgencyRow` navigiert nach `/overview?section=recommended`).
                   Vorher landete er auf einer ZUGEKLAPPTEN Sektion: die Antwort auf
                   seine Frage lag hinter einem geschlossenen Aufklapper.
                   `CollapsibleSection` ist unkontrolliert (`useState(defaultExpanded)`),
                   und die Seite montiert bei Navigation neu -- der Anfangswert genuegt,
                   es braucht keinen gesteuerten Zustand. Ohne den Parameter bleibt
                   alles wie bisher zugeklappt.

                2. Die Zahl im Titel zaehlte LIEFERANTENGRUPPEN, die Kachel zaehlt SKUs.
                   Aus "12" auf der Kachel wurde einen Klick spaeter "(4)", beides als
                   dieselbe Arbeitsmenge dargestellt. Jetzt steht die Einheit dabei. */}
            <CollapsibleSection
              title={`Recommended purchase orders${
                recommendedOrders.length
                  ? ` (${recommendedOrders.reduce((n, g) => n + (g.recommended_orders?.length || 0), 0)} SKUs \u00b7 ${recommendedOrders.length} vendors)`
                  : ''
              }`}
              // 🔴 `key` erzwingt ein Neuaufsetzen, wenn sich NUR der Parameter aendert.
              // `CollapsibleSection` ist unkontrolliert: ohne Neuaufsetzen bliebe der
              // Anfangswert von der ersten Montierung stehen, und ein Klick auf die
              // Kachel WAEHREND man schon auf /overview steht taete gar nichts.
              key={searchParams.get('section') || 'ohne'}
              defaultExpanded={searchParams.get('section') === 'recommended'}
            >
              {empfehlungsLage === 'laedt' && (
                <Stack spacing={1} sx={{ py: 1 }}>
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                </Stack>
              )}
              {empfehlungsLage === 'fehler' && (
                <Alert
                  severity="error"
                  action={<Button size="small" onClick={ladeEmpfehlungen}>Retry</Button>}
                >
                  Could not load recommended orders. This is not the same as having none —
                  the list below is unknown, not empty.
                </Alert>
              )}
              {empfehlungsLage === 'da' && (
                <RecommendedOrdersSection
                  groups={recommendedOrders}
                  onDecline={handleDecline}
                  onAccept={handleAccept}
                  onPreview={handlePreview}
                />
              )}
            </CollapsibleSection>

            {ersteLadung ? (
              <POOverviewSkeleton />
            ) : (
              <Stack spacing={1.5}>
                {/* 🔴 Der zweite Seitentitel ist raus (Konzept §7.1g).
                    „Purchase Orders" stand hier als `subtitle1` 60 px unter dem
                    `ViewHeader`, der dasselbe schon als `h5` sagt — und das Wort
                    „purchase orders" erschien damit VIER Mal ueber der ersten
                    Datenzeile: Seitenleiste, Breadcrumb, ViewHeader, Sektionslabel. */}
                <Panel
                  pad="none"
                  // 🔴 R16 W5 (Nachbesserung 1, 2026-10-14): `clip` — Konzept §2.1,
                  // Zone 5 nennt `Panel pad="none" clip` woertlich, und die
                  // Musterroute setzt es (`ListenMuster.jsx:543`). Ohne es malt die
                  // volle Breite der Fusszeile (`TableFooterSimple`, `bgcolor:
                  // background.paper`, `borderTop`) die unteren Ecken des
                  // `PANEL_RADIUS` viereckig zu (`Panel.jsx:58` beschreibt den
                  // Zweck selbst). 🔴 Der klebende Kopf und das waagerechte Rollen
                  // sitzen IN `TableViewport` (`TableContainer`, `overflow: auto`)
                  // — `overflow: hidden` am Panel ist dafuer kein Vorfahre im
                  // Scrollsinn, beide bleiben unberuehrt. Der A/B prueft es am Bild.
                  clip
                  // 🔴 R16 W2 / §8.2: „die alten Zeilen bleiben, `aria-busy` am
                  // Daten-Panel". Nur beim AUFFRISCHEN gesetzt (beim ersten Aufbau
                  // steht das Geruest, dann gibt es dieses Panel nicht).
                  aria-busy={loading || undefined}
                  data-testid="liste-panel"
                >
                  <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    {/* N8.5/P9: der GRUND der Sperre steht sichtbar DIREKT ueber
                        der Leiste. Der Satz sagt etwas anderes als der
                        Leerzustand der Liste: der `EmptyState` nennt, WAS
                        fehlschlug, dieser `Alert` nennt, WARUM die Bedienelemente
                        gesperrt sind. */}
                    {sperre && (
                      <Alert
                        severity="warning"
                        sx={{ mb: SP.card }}
                        data-testid="erster-aufbau-gesperrt"
                      >
                        Search and filters are locked until the list loads. Use Try again below.
                      </Alert>
                    )}
                    {/* 🔴 R15r2 W3 (Nachbesserung 2, 2026-10-14): der Fehler des
                        AUFFRISCHENS ist NICHT der des ersten Aufbaus. Die Zeilen
                        stehen (der Fehler-`EmptyState` erscheint nur ohne
                        Zeilen), die Bedienelemente bleiben bedienbar — also nennt
                        dieser Hinweis den Fehler UND den Weg zurueck, statt eine
                        Sperre zu behaupten, die es nicht gibt. Der Knopf heisst
                        „Refresh" (Tooltip `RefreshButton.jsx:88`), sein
                        zugaenglicher Name „Refresh purchase orders".
                        🔴 R16r3 W2 (Nachbesserung 3): sein Satz („The last loaded
                        rows are still shown") verlangt SICHTBARE Zeilen. Trifft
                        der Filter null Zeilen, steht unten der Fehler-Leerzustand
                        und der Satz waere falsch — deshalb das dritte Gatter.
                        Beide `Alert`s schliessen einander aus (`sperre` ⇔
                        `jeGeladen`), es steht nie eine doppelte `role="alert"`. */}
                    {Boolean(error) && jeGeladen && paginatedRows.length > 0 && (
                      <Alert
                        severity="warning"
                        sx={{ mb: SP.card }}
                        data-testid="auffrischen-gescheitert"
                      >
                        Could not refresh the list. The last loaded rows are still shown. Use Refresh to try again.
                      </Alert>
                    )}
                    {/* 🔴 EINE Werkzeugleiste fuer beide Breiten (Konzept §7.1f),
                        in den drei Zonen der Listen-Anatomie (§2.1): Z1 Suche ·
                        Facette · Zaehler · Auffrischen · Seitenaktionen, Z2 die
                        Status-Werte-Reihe, Z3 die Chipzeile. Sie ist der EINZIGE
                        Ort, an dem die Ansicht ihre Werkzeuge baut — der
                        Schubkasten und der „Clear filters"-Knopf sind fort
                        (§1.2 Wegfall-Tabelle). */}
                    <ListToolbar
                      shortcut="/"
                      // 🔴 R15/Fotobefund (Nachbesserung 3): auf der
                      // Kartenhälfte (< 900 px) trägt Z1 die KOMPAKTE Form —
                      // Suche + „Filters", Vendor im Blatt (`blattKriterien`),
                      // Zähler in Z3. Vorher stand bei 768 px die
                      // Desktop-Z1 mit Facette und Zähler: sie drückte das
                      // Suchfeld auf ~0 px (Foto). Die Vendor-Facette bleibt
                      // erreichbar, weil sie schon als Blatt-Kriterium steht.
                      kompakt={karteStattTabelle}
                      search={{
                        value: state.q ?? '',
                        onChange: (v) => set('q', v),
                        onCommit: (v) => set('q', v),
                        placeholder: 'Search PO or vendor…',
                        // P5: der zugängliche Name beginnt mit „Search".
                        label: 'Search purchase orders',
                        // P5/N7.1: die Rolle des Feldes. `SearchTrigger` setzt
                        // `type="search"` absichtlich nicht — ohne die Rolle ist
                        // das Feld eine `textbox`, und P5/P16/P23/P34 fanden
                        // „0 Suchfelder".
                        inputRole: 'searchbox',
                        disabled: sperre,
                        // 🔴 N2.9/N7.3 — „die Suche FUELLT den Rest von Z1".
                        // `ListToolbar` setzt `flex: 1` am umgebenden Kasten;
                        // `SearchTrigger` reicht `sx` an das `TextField`-
                        // WURZELELEMENT weiter (`MuiFormControl-root`), und DAS
                        // trug kein `flex`. Es stand deshalb auf seiner
                        // Eigenbreite (`minWidth: 220`), waehrend der Kasten schon
                        // wuchs — gemessen im A/B: Suchfeld 220 px, 444 px Luecke
                        // bis zur ersten Facette (P24 rot). Dieselbe Zeile traegt
                        // die Musterroute (`ListenMuster.jsx:595`), samt
                        // Begruendung; `minWidth: 0` nimmt ZUGLEICH den 220-px-
                        // Boden heraus, der die Leiste auf schmalen Breiten
                        // ueberlaufen liess (P1).
                        // 🔴 Rein additiv: das `sx` gehoert dem AUFRUFER, nicht
                        // dem Baustein — die uebrigen Aufrufer bleiben unberuehrt.
                        sx: { flex: 1, minWidth: 0 },
                      }}
                      facets={facetten}
                      moreFilters={{
                        disabled: sperre,
                        criteria: blattKriterien,
                        sort: blattSort,
                        // §4.2: im FEHLER traegt die Leiste keinen Zaehler — der
                        // Leerzustand spricht. `null` waere „unbekannt" und
                        // zeichnete „Show results".
                        // 🔴 R15r2 W3: das Gatter ist `sperre`, nicht
                        // `leerGrund === 'error'` — beim gescheiterten
                        // AUFFRISCHEN stehen die Zeilen, und der Zaehler zaehlt
                        // sie. Nur der nie geladene Erstaufbau traegt keinen.
                        resultCount: sperre ? undefined : sortedRows.length,
                        activeCount: blattAktiv,
                        onClearAll: clearAll,
                        label: 'Filters',
                        title: 'Filters',
                      }}
                      quickFilters={statusReihe}
                      // 🔴 §4.2/N4.5: der FEHLER des ERSTEN Aufbaus traegt keinen
                      // Zaehler — der Leerzustand spricht. Eine `0` hier waere die
                      // Behauptung „null Bestellungen", wo „konnte nicht laden"
                      // gilt (Regel 22 Nr. 3).
                      // 🔴 R15r2 W3: das Gatter ist `sperre`, nicht `error` —
                      // beim gescheiterten AUFFRISCHEN zaehlt der Zaehler die
                      // weiterhin sichtbaren Zeilen.
                      count={sperre ? undefined : {
                        noun: 'purchase orders',
                        n: sortedRows.length,
                        m: enrichedOrders.length,
                      }}
                      refresh={(
                        <RefreshButton
                          ariaLabel="Refresh purchase orders"
                          onClick={neuLaden}
                          // 🔴 R16 W2: `loading` traegt jetzt wirklich etwas —
                          // beim Auffrischen bleibt die Flaeche montiert (s.
                          // `ersteLadung`), der Knopf dreht und traegt
                          // `aria-busy`. Vorher demontierte `loading ? <Skelett/>`
                          // den Knopf, der Prop war beim Rendern immer `false`.
                          // 🔴 `firstLoad` wird hier absichtlich NICHT gesetzt —
                          // die Begruendung ist seit der Nachbesserung 3 aber eine
                          // andere. Sie lautete „der Knopf existiert nur bei
                          // offenem Gatter (`!ersteLadung`), `!jeGeladen` ist dann
                          // per Konstruktion `false`"; das gilt nicht mehr, denn im
                          // gescheiterten Erstaufbau steht der Knopf sehr wohl
                          // (`jeGeladen` `false`, `loading` `false`). Er braucht
                          // den Prop dort nicht: die Flaeche ist genau dann
                          // demontiert (Geruest), wenn `loading && !jeGeladen` —
                          // der Knopf kann also nie sichtbar UND im ersten Aufbau
                          // sein. `firstLoad={!jeGeladen}` wuerde ihn im
                          // Fehlerfall zusaetzlich sperren; das ist ein eigener
                          // Schnitt (N8.5/P9 sperrt Suche, Facetten, Pillen und
                          // Oeffner — der ⟳ bleibt der zweite Weg neben „Try
                          // again").
                          loading={loading}
                        />
                      )}
                      actions={seitenAktionen}
                      actionsLabel="More purchase order actions"
                      query={{ chips, clearAll }}
                    >
                      {/* 🔴 N10.4/P13: ein UNBEKANNTER Wert (`?status=foo`) wird
                          verworfen UND EINMAL gemeldet — stilles Ignorieren ist
                          der Defekt, den die Probe misst. Der Hinweis traegt die
                          Hausmarke `ignoriert-hinweis` (`selektoren.js`), steht
                          INNERHALB der Leiste (also in `liste-panel`) und
                          verschwindet mit der bereinigten Adresse von selbst
                          (`useListQuery` merkt sich den Durchlauf). */}
                      {query.ignoredValues.length > 0 && (
                        <Alert
                          severity="info"
                          data-testid="ignoriert-hinweis"
                          sx={{ mx: 2, mt: 1 }}
                        >
                          {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
                        </Alert>
                      )}
                      {karteStattTabelle ? (
                    /* ─── Mobile: card list (G2b, §2.6/N11.1) ─── */
                    /* 🔴 Die Marke `liste-karte` stempelt kein geteilter Baustein
                       (§5.3/B2) — die Ansicht setzt sie von Hand, wie
                       `liste-panel`/`liste-tabelle`/`liste-fuss` daneben. P15
                       greift ueber sie zu (`SEL.karte`). */
                    <Box data-testid="liste-karte" sx={{ mt: 1 }}>
                      <Stack spacing={1}>
                      {paginatedRows.map(po => {
                        // 🔴 Der Fristhinweis kommt aus `daysLabel` in `POStatusBar`
                        // (Konzept §7.1d / Posten #12) — dieselbe Funktion, die die
                        // Detailseite schon benutzt, also EINE Lesart von „15d
                        // overdue"/„Today"/„in 3d" statt einer zweiten hier.
                        const frist = daysLabel(po.expected_arrival);
                        return (
                          // 🔴 G2b (§2.6/N11.1): die handgerollte Karte ist durch
                          // den gemeinsamen Baustein ersetzt — „keine
                          // Kartenkomponente je Ansicht".
                          //
                          // 🔴 `slotOrder="chips-first"` ist die EINE Stelle, an
                          // der `/overview` N11.2 folgt (Titel → Kennung →
                          // **Status** → Kerndaten). Der Baustein bleibt bei
                          // seiner Vorgabe (`'meta-first'`, G1b).
                          //
                          // 🔴 **Der Titel IST die Kennung** (Konzept §G2:
                          // „PO-Nummer (Titel = Kennung)"). Eine Bestellung hat
                          // keinen zweiten Namen; die Nummer ist der Datensatz
                          // und der Weg hinein — dieselbe Anatomie wie die
                          // Faelligkeitskarte (`rma/RMAFaelligkeit.jsx:460-464`).
                          // Sie wird deshalb EINMAL gerendert, im Titelplatz; ein
                          // zweiter Kennungs-`id`-Platz darunter waere dieselbe
                          // Zeichenkette zweimal. Das ist eine ABWEICHUNG vom
                          // Woertlaut der Konzeptzelle („Kerndaten *Vendor*,
                          // *Total*, *Expected*"), und sie ist benannt: der
                          // Lieferant steht als erstes Kerndatum im `meta`-Block,
                          // nicht im Titel.
                          <MobileRecordCard
                            key={po.po_id}
                            // Hausform wie `produkt-karte-<sku>`: die Karte ist
                            // ueber ihren Datensatz adressierbar, nicht ueber
                            // ihre Rolle.
                            data-testid={`po-karte-${po.po_id}`}
                            slotOrder="chips-first"
                            // P15 greift ueber diese zwei Marken zu (§15). Sie
                            // sind noetig, weil N11.2 eine Aussage ueber die
                            // REIHENFOLGE ist (Status vor Kerndaten) — ohne zwei
                            // adressierbare Plaetze ist sie im Browser nicht
                            // messbar.
                            fieldTestIds={{
                              chips: `karte-chips-${po.po_id}`,
                              meta: `karte-meta-${po.po_id}`,
                            }}
                            title={(
                              <MuiLink
                                component={Link}
                                to={`/purchase_order/${po.po_id}`}
                                color="inherit"
                                underline="hover"
                                sx={KARTE_TITEL_SX}
                                // 🔴 Die Marke sitzt am TRAEGER der Kennung —
                                // und das ist hier der Titel (`KARTE_TITEL_SX`
                                // erklaert, warum). P15 liest sie
                                // (`tests/listen/bestellungen.spec.ts`); ohne
                                // sie müsste die Probe die Klemme über eine
                                // Schachtelungstiefe raten, und eine Vermutung
                                // ist keine Messung.
                                data-testid={`karte-titel-${po.po_id}`}
                              >
                                {po.po_id}
                              </MuiLink>
                            )}
                            chips={(
                              // 🔴 Dieselbe `Chip`-Variante wie die Desktop-Zeile
                              // (N11.3: „Chips in derselben Variante").
                              //
                              // 🔴 **Der Lieferantenleistungs-Chip der
                              // Statuszelle steht hier NICHT — benannt, nicht
                              // vergessen.** Die Konzeptzelle §G2 nennt die
                              // Felder der Karte vollstaendig: „PO-Nummer
                              // (Titel = Kennung) · Status · Kerndaten *Vendor*,
                              // *Total*, *Expected* · ⋮"; der Leistungs-Chip ist
                              // nicht darunter. Und die handgerollte Karte, die
                              // G2b ersetzt, trug ihn ebenfalls nicht — es geht
                              // hier also **nichts verloren**, was die Karte
                              // hatte. 🔴 Der zweite Grund ist mechanisch und
                              // gemessen: `scripts/check_ui_tokens.py` zaehlt
                              // `<Chip` als `raw_chip` (Regel 22 §„Component
                              // rules" 1 — ein Chip-Bauteil, nicht zwei), und ein
                              // zweiter `<Chip` hier haette die Baseline von 453
                              // auf 454 gehoben. Die Baseline darf nur SINKEN;
                              // wer sie hebt, braucht `--accept-base-growth` —
                              // fuer ein Feld, das die Karte nie hatte, ist das
                              // der falsche Preis.
                              <Chip
                                label={PO_STATUS_LABEL[po.status] || po.status}
                                size="small"
                                color={STATUS_COLOR_OV[po.status] || 'default'}
                              />
                            )}
                            meta={(
                              <Stack direction="row" spacing={SP.chip} useFlexGap flexWrap="wrap" alignItems="baseline">
                                {/* Die drei Kerndaten des Konzepts (§G2): Vendor,
                                    Total, Expected. */}
                                <Typography variant="caption" color="text.secondary">
                                  {po.vendorLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {`Total ${betragText(po)}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {`Expected ${formatDate(po.expected_arrival)}`}
                                </Typography>
                                {/* Die Frist steht unter „Expected", nicht als
                                    eigener Balken: der Balken behauptete, `Paid`
                                    sei 40 % einer Bestellung — die Abschnitte
                                    sind aber nicht gleich lang, die Zahl war eine
                                    Fiktion im Gewand einer Messung. */}
                                {frist && (
                                  <Typography variant="caption" sx={{ color: frist.color }}>
                                    {frist.text}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                            actions={(
                              // 🔴 **Keine `onPointerDown`-Huelle** (R15 W1 =
                              // R16 W2, Nachbesserung 1). Sie war aus der
                              // Musterroute uebernommen, wo sie einen echten
                              // Mechanismus abwehrt: dort traegt die
                              // KARTENWURZEL `gesteFuer(r.id)`
                              // (`design/muster/ListenMuster.jsx:1073-1085`).
                              // `MobileRecordCard` und `Panel` haben KEINEN
                              // Zeigerhandler (`grep -n "onPointerDown\|onClick"
                              // MobileRecordCard.jsx` → leer), und der Titel ist
                              // der einzige Weg hinein (`@dontUseFor`). Die
                              // Huelle konnte deshalb nichts unterbinden — eine
                              // Zusicherung ohne Gegenstand, mit einer
                              // Begruendung, die diese Seite falsch beschrieb.
                              // Titel-Link und ⋮ loesen je genau EINE Handlung
                              // aus; `POOverview.breiten.test.jsx` B.7 misst es.
                              <RowActionsMenu
                                label={`Actions for PO ${po.po_id}`}
                                actions={[
                                  {
                                    key: 'open',
                                    label: 'Open details',
                                    onClick: () => nav(`/purchase_order/${po.po_id}`),
                                  },
                                ]}
                              />
                            )}
                          />
                        );
                      })}
                      {!paginatedRows.length && (
                        <Box sx={{ py: 4 }}>
                          {leerzustand}
                        </Box>
                      )}
                      </Stack>
                    </Box>
                  ) : (
                  // 🔴 Die Marke `liste-tabelle` stempelt kein geteilter Baustein
                  // (§5.3/B2) — sie sitzt hier am Traeger des Desktop-Baums
                  // (P19/P20/P32/P33 lesen sie).
                  //
                  // 🔴 G0b.5: der Kasten misst die **Container-Breite** (BW4,
                  // `useContainerWidth`) — die rohe Innenbreite, aus der der Kanal
                  // `min(Behaelter, Budget)` bildet. Sein `ref` sitzt hier und
                  // nicht am `TableViewport`: gemessen wird der RAUM, nicht die
                  // gedeckelte Tabelle (sonst maesse sich der Deckel selbst).
                  <Box ref={tabelleRef} data-testid="liste-tabelle">
                  <TableViewport
                    footerPadPx={0}
                    // 🔴 Die MELDUNG (BW6): `priorities` schaltet den Kanal ein
                    // (und damit `SEARCH_MAX` als Suchdeckel, NW4.2:648 — **kein
                    // eigenes `searchMax`**, §2.6/W10), die rohe Behaelterbreite
                    // reist als `containerWidth` mit.
                    priorities={SPALTEN}
                    containerWidth={behaelterBreite}
                    // 🔴 N9.6 — beide zusammen: die Kennungsspalte bleibt beim
                    // Rollen im Bild (`stickyFirstColumn`), und der weiche Rand
                    // sagt, dass rechts Spalten weitergehen (`edgeHint`). Die
                    // linke Haelfte des Randes schaltet `stickyFirstColumn` ab —
                    // die klebende Spalte ist der linke Anker.
                    stickyFirstColumn
                    edgeHint
                    // 🔴 Das ANWENDEN der Tabellenbreite = min(Behaelter, Budget)
                    // (§4.1:568) — der Verbraucher deckelt seine Tabelle damit; der
                    // Baustein meldet nur (PW4/PW4b).
                    sx={tabellenMax > 0 ? { maxWidth: tabellenMax } : undefined}
                  >
                    <Table
                      size="small"
                      sx={{
                        // 🔴 N9.5: **Spalten springen nicht** — `tableLayout:
                        // 'fixed'` mit erklaerten Breiten fuer die Kernspalten.
                        // Ohne die feste Satzart rechnet der Browser die
                        // Spaltenbreiten aus ALLEN Zeilen; im Leerzustand fehlen
                        // die Datenzeilen, die Spalten ziehen sich auf die
                        // Kopfbreite zusammen, und P33 mass genau diesen Versatz.
                        // 🔴 Der alte Rollfall `minWidth: 900` ist FORT (§1.2) —
                        // die Breite kommt aus Prioritaeten + Budget. 🔴 Aber
                        // eine UNTERGRENZE bleibt (R15 B1 / R16 W4): die Summe
                        // der Kernspalten-Minima in der proportionalen
                        // Verteilung. Darunter rollt der `TableContainer`
                        // waagerecht, statt `Expected`/`Total` unter ihr
                        // `COLUMN_MAX.min` zu druecken; erst dadurch greifen
                        // `stickyFirstColumn` und `edgeHint` ueberhaupt.
                        minWidth: spaltenMinBreite,
                        tableLayout: 'fixed',
                        '& thead th': {
                          bgcolor: (theme) => theme.palette.grey[50],
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          fontWeight: 600,
                        },
                      }}
                    >
                      <TableHead>
                        {/* 🔴 Die Sichtbarkeit kommt aus `useColumnPriority`
                            (§2.6): `kern` ist immer da, `lg` weicht unter 1024
                            Containerbreite. **Eine** Quelle fuer Kopf UND Rumpf
                            (`sichtbar`), sonst zeigte der Kopf eine Spalte an,
                            die die Zeilen nicht fuellen (oder umgekehrt).
                            🔴 Und die erklaerte Breite steht an der KOPFZELLE:
                            `table-layout: fixed` nimmt die Breiten aus der
                            ersten Zeile (N9.5). */}
                        <TableRow>
                          <TableCell
                            sortDirection={sortBy === 'po_id' ? sortOrder : false}
                            sx={spaltenBreite('po_id')}
                          >
                            <TableSortLabel
                              active={sortBy === 'po_id'}
                              direction={sortBy === 'po_id' ? sortOrder : 'asc'}
                              onClick={() => handleSort('po_id')}
                            >
                              PO ID
                            </TableSortLabel>
                          </TableCell>
                          {sichtbar.has('vendorLabel') && (
                          <TableCell
                            sortDirection={sortBy === 'vendorLabel' ? sortOrder : false}
                            sx={spaltenBreite('vendorLabel')}
                          >
                            <TableSortLabel
                              active={sortBy === 'vendorLabel'}
                              direction={sortBy === 'vendorLabel' ? sortOrder : 'asc'}
                              onClick={() => handleSort('vendorLabel')}
                            >
                              Vendor
                            </TableSortLabel>
                          </TableCell>
                          )}
                          {sichtbar.has('order_date') && (
                          <TableCell
                            sortDirection={sortBy === 'order_date' ? sortOrder : false}
                            sx={spaltenBreite('order_date')}
                          >
                            <TableSortLabel
                              active={sortBy === 'order_date'}
                              direction={sortBy === 'order_date' ? sortOrder : 'asc'}
                              onClick={() => handleSort('order_date')}
                            >
                              Order Date
                            </TableSortLabel>
                          </TableCell>
                          )}
                          <TableCell
                            sortDirection={sortBy === 'expected_arrival' ? sortOrder : false}
                            sx={spaltenBreite('expected_arrival')}
                          >
                            <TableSortLabel
                              active={sortBy === 'expected_arrival'}
                              direction={sortBy === 'expected_arrival' ? sortOrder : 'asc'}
                              onClick={() => handleSort('expected_arrival')}
                            >
                              Expected
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="right"
                            sortDirection={sortBy === 'totalEur' ? sortOrder : false}
                            sx={spaltenBreite('totalEur')}
                          >
                            <TableSortLabel
                              active={sortBy === 'totalEur'}
                              direction={sortBy === 'totalEur' ? sortOrder : 'asc'}
                              onClick={() => handleSort('totalEur')}
                            >
                              {/* 🔴 Kopf „Total", NICHT „Total (€)" (§2.6): die
                                  Spalte ist nicht immer Euro — nicht
                                  umgerechnete Zeilen nennen ihren eigenen Code
                                  (`betragText`). Der Satz „Total (€)" ueber
                                  einem Dollar-Betrag waere eine falsche Aussage.
                                  Die Erklaerung bleibt im `HelpTooltip`. */}
                              Total
                            </TableSortLabel>
                            {/* The permanent half of the announcement. A changelog entry has
                                no reader on the surface where the number moved — nothing in
                                the SPA serves docs/changelog/. One icon, one header cell,
                                zero rows touched, zero scan cost, and it survives the week
                                the changelog is read in. */}
                            <HelpTooltip
                              title={
                                <>
                                  Converted as <strong>EUR = native × conversion_rate</strong>.
                                  Purchase orders without a usable stored rate are shown in
                                  their own currency (e.g. $) and are <strong>not</strong>{' '}
                                  converted — the figure is the native amount.
                                </>
                              }
                            />
                          </TableCell>
                          <TableCell
                            sortDirection={sortBy === 'status' ? sortOrder : false}
                            sx={spaltenBreite('status')}
                          >
                            <TableSortLabel
                              active={sortBy === 'status'}
                              direction={sortBy === 'status' ? sortOrder : 'asc'}
                              onClick={() => handleSort('status')}
                            >
                              Status
                            </TableSortLabel>
                          </TableCell>
                          {sichtbar.has('itemCount') && (
                          <TableCell
                            align="right"
                            sortDirection={sortBy === 'itemCount' ? sortOrder : false}
                            sx={spaltenBreite('itemCount')}
                          >
                            <TableSortLabel
                              active={sortBy === 'itemCount'}
                              direction={sortBy === 'itemCount' ? sortOrder : 'asc'}
                              onClick={() => handleSort('itemCount')}
                            >
                              # Items
                            </TableSortLabel>
                          </TableCell>
                          )}
                          {/* 🔴 Die ⋮-Spalte traegt `BEDIENBREITE.aktion` (140,
                              §2.6). Ohne die erklaerte Breite bekaeme sie mit
                              `table-layout: fixed` 0 px — die Zeilenaktion laege
                              ueber der letzten Datenspalte (F1). */}
                          <TableCell sx={{ width: BEDIENBREITE.aktion }} />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((po) => {
                          const frist = daysLabel(po.expected_arrival);
                          const perf = po.status === 'In Delivery' ? vendorPerf[po.vendor_id] : null;
                          return (
                            <TableRow
                              key={po.po_id}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => nav(`/purchase_order/${po.po_id}`)}
                            >
                              <TableCell sx={spaltenBreite('po_id')}>
                                {/* 🔴 Die Kennung ist jetzt ein echter Link (Konzept
                                    §7.1b). Vorher war die Zeile NUR ein
                                    `onClick`-Handler: nicht fokussierbar, nicht
                                    mit der Tastatur ausloesbar, kein
                                    Mittelklick, kein „in neuem Tab oeffnen" —
                                    und genau das ist in einem
                                    Bestellwerkzeug der Normalfall. Der
                                    Zeilen-Handler bleibt als Bequemlichkeit. */}
                                <Link
                                  to={`/purchase_order/${po.po_id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
                                >
                                  {po.po_id}
                                </Link>
                              </TableCell>
                              {sichtbar.has('vendorLabel') && (
                              <TableCell sx={spaltenBreite('vendorLabel')}>{po.vendorLabel}</TableCell>
                              )}
                              {sichtbar.has('order_date') && (
                              <TableCell sx={spaltenBreite('order_date')}>{fmtDate(po.order_date)}</TableCell>
                              )}
                              <TableCell sx={spaltenBreite('expected_arrival')}>
                                <Typography variant="body2" component="span">
                                  {fmtDate(po.expected_arrival)}
                                </Typography>
                                {/* Dieselbe Fristlesart wie die Mobilkarte und die
                                    Detailseite. Vorher stand hier ein Chip, der
                                    NUR bei `In Delivery` erschien — eine
                                    Bestellung, deren Frist in `Order placed`
                                    ablief, sah aus wie eine puenktliche. */}
                                {frist && (
                                  <Typography variant="caption" display="block" sx={{ color: frist.color, lineHeight: 1.3 }}>
                                    {frist.text}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={spaltenBreite('totalEur')}>
                                {/* 🔴 Dieselbe Funktion wie die Mobilkarte
                                    (`betragText`) — „Betraege auf Karte und
                                    Zeile textgleich" ist damit eine Eigenschaft
                                    der Konstruktion, nicht zweier Abschriften
                                    (§2.6). Der Satz zum Waehrungscode steht an
                                    `betragText`. */}
                                {betragText(po)}
                              </TableCell>
                              <TableCell sx={spaltenBreite('status')}>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                  <Chip
                                    label={PO_STATUS_LABEL[po.status] || po.status}
                                    size="small"
                                    color={STATUS_COLOR_OV[po.status] || 'default'}
                                  />
                                  {/* 🔴 Die Lieferantenleistung ist aus der eigenen Spalte
                                      hierher gezogen (Konzept §7.1a / Posten #10). Sie
                                      fuellte sich nur bei `In Delivery` — als Spalte war
                                      sie also fuenf Sechstel der Zeit leer, waehrend die
                                      Statusspalte, die sie qualifiziert, daneben stand. */}
                                  {perf && (
                                    <Chip
                                      label={
                                        perf.significant_deviation
                                          ? `${perf.deviation_days > 0 ? '+' : ''}${perf.deviation_days}d`
                                          : 'On time'
                                      }
                                      size="small"
                                      variant="outlined"
                                      color={perf.significant_deviation ? 'warning' : 'success'}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              {sichtbar.has('itemCount') && (
                              <TableCell align="right" sx={spaltenBreite('itemCount')}>
                                {po.itemCount ?? (Array.isArray(po.order_items) ? po.order_items.length : '–')}
                              </TableCell>
                              )}
                              <TableCell sx={{ width: BEDIENBREITE.aktion }}>
                                <RowActionsMenu
                                  label={`Actions for PO ${po.po_id}`}
                                  actions={[
                                    {
                                      key: 'open',
                                      label: 'Open details',
                                      onClick: () => nav(`/purchase_order/${po.po_id}`),
                                    },
                                  ]}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {!paginatedRows.length && (
                          <TableRow>
                            {/* 🔴 Die Zahl folgt der SICHTBARKEIT: die
                                Leerzustandszeile deckt die sichtbaren
                                Datenspalten UND die ⋮-Spalte. Ein festes 8
                                deckte auf schmalem Behaelter zu viel (die
                                Tabelle laeuft dann ueber den Rand) und auf
                                keinem Fall zu wenig. */}
                            <TableCell colSpan={sichtbareSpalten.length + 1} sx={{ py: 2 }}>
                              {leerzustand}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableViewport>
                  </Box>
                  )} {/* end desktop table / mobile card conditional */}
                  {/* 🔴 Die Marke `liste-fuss` stempelt kein geteilter Baustein
                      (§5.3/B2). 🔴 `stickyOnMobile={false}` ist ein OPT-IN, das
                      GESETZT werden muss: die Vorgabe des Bausteins ist `true`
                      (`TableFooterSimple.jsx:36`), und ohne die Zeile deckt die
                      schwebende Fusszeile mobil die Zone ueber der Unterkante
                      (§2.1/BS17).
                      🔴 Der `leftSlot` („N purchase orders") ist FORT (§1.2):
                      §4.2/N4.3 verlangt GENAU EINEN Zaehler, und der steht in Z1
                      (`ResultCount`). Der Baustein dokumentiert `leftSlot`
                      ohnehin als „nur fuer Summen". */}
                  {/* 🔴 R16 W1 / N8.7 („Kein leerer Fußzeilen-Behälter: die
                      Fußzeile steht nur mit Inhalt"): die Marke steht UNBEDINGT,
                      der Baustein gibt bei `count === 0` aber `null` zurueck
                      (`TableFooterSimple.jsx:38`) — im no-match- und im
                      Fehlerzustand stand damit ein LEERER Kasten. Dieselbe
                      Bedingung traegt die Musterroute (`ListenMuster.jsx:1126`:
                      `!leerGrund && sortiert.length > 0`). Gemessen wird es von
                      `messeP33` (Marke fehlt ODER traegt Text) und von
                      `POOverview.leerzustand.test.jsx` (Rotweg). */}
                  {sortedRows.length > 0 && (
                    <Box data-testid="liste-fuss">
                      <TableFooterSimple
                        count={sortedRows.length}
                        page={seite}
                        onPageChange={(p) => set('page', p + 1)}
                        rowsPerPage={ROWS_PER_PAGE}
                        maxWidth="100%"
                        stickyOnMobile={false}
                      />
                    </Box>
                  )}
                </ListToolbar>
                  </Box>
                </Panel>
              </Stack>
            )}
          </Stack>

          <RecommendationPreviewDrawer
            open={drawerOpen}
            onOpen={() => setDrawerOpen(true)}
            onClose={() => setDrawerOpen(false)}
            group={activeGroup}
            products={products}
            produktLage={produktLage}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDiscontinued={handleDiscontinued}
          />

          <DeclinedProductsDrawer
            open={declinedDrawerOpen}
            onClose={() => setDeclinedDrawerOpen(false)}
          />

          {/* 🔴 Der eigene Lieferanten-Schubkasten ist FORT (§1.2 Wegfall-Tabelle):
              die Facette `BottomSheetMultiSelect` (Z1, `desktopForm="pill"`) traegt
              dieselbe Auswahl, und auf dem Telefon oeffnet DERSELBE Baustein sein
              Blatt. Zwei Flaechen fuer eine Achse waeren die Doppelquelle, gegen
              die die Zonen angetreten sind. */}

        </PageContainer>
      </Box>
    </Fade>
  );
}
